import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  TrendUpIcon,
  SealCheckIcon,
  PackageIcon,
  StarIcon,
  UserCircleIcon,
  HandshakeIcon,
} from "@phosphor-icons/react";
import VerifiedBadge from "@/components/VerifiedBadge";
import VerifiedBuyersBanner from "@/components/home/VerifiedBuyersBanner";
import VerifiedMarquee from "@/components/home/VerifiedMarquee";

/* ─── helpers ─────────────────────────────────────────────────────────── */

function formatUSD(n) {
  if (!n && n !== 0) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `$${(n / 1_000).toFixed(0)}K`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n)}`;
}

function formatCount(n) {
  if (!n && n !== 0) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(0)}K`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${n}`;
}

/** Compact "2m ago" / "5h ago" / "3d ago" formatter. */
function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

/**
 * Smooth count-up: tweens from `from` → `to` over `duration` ms.
 * Preserves nice formatting by calling `format(currentValue)` each frame.
 */
function useCountUp(target, { duration = 800, format = (v) => v } = {}) {
  const [display, setDisplay] = useState(target);
  const fromRef = useRef(target);
  useEffect(() => {
    const from = fromRef.current;
    const to = target;
    if (from === to) return;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutQuad
      const eased = 1 - (1 - t) * (1 - t);
      const v = from + (to - from) * eased;
      setDisplay(v);
      if (t < 1) raf = requestAnimationFrame(step);
      else fromRef.current = to;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return format(display);
}

/* ─── tile ────────────────────────────────────────────────────────────── */

function StatTile({ icon: Icon, raw, value, label, tone, testid, changed }) {
  return (
    <div
      data-testid={testid}
      className={`relative transition-transform duration-500 ${
        changed ? "scale-[1.02]" : ""
      }`}
    >
      <Icon size={22} weight="fill" className={tone} />
      <div className="font-display font-black text-4xl lg:text-5xl tracking-tighter mt-2 tabular-nums">
        {value}
      </div>
      <div className="text-xs text-zinc-400 uppercase tracking-[0.14em] font-semibold mt-1">
        {label}
      </div>
    </div>
  );
}

/* ─── section ─────────────────────────────────────────────────────────── */

export default function HomeStats() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [flash, setFlash] = useState(0);
  const [justVerifiedIds, setJustVerifiedIds] = useState(new Set());
  // eslint-disable-next-line no-unused-vars
  const [_tick, setTick] = useState(0); // forces timeAgo re-render every 30s
  const prevRef = useRef(null);
  const seenIdsRef = useRef(null); // null = first load; then Set of known ids

  useEffect(() => {
    let alive = true;
    const fetchAll = async () => {
      try {
        const [s, r] = await Promise.all([
          api.get("/stats/home"),
          api.get("/stats/recently-verified", { params: { limit: 24 } }),
        ]);
        if (!alive) return;
        const moved =
          prevRef.current &&
          (prevRef.current.gmv_usd !== s.data.gmv_usd ||
            prevRef.current.verified_sellers !== s.data.verified_sellers ||
            prevRef.current.verified_buyers !== s.data.verified_buyers ||
            prevRef.current.items_shipped !== s.data.items_shipped);
        if (moved) setFlash((f) => f + 1);
        prevRef.current = s.data;
        setStats(s.data);

        const items = r.data.items || [];
        setRecent(items);

        // Detect brand-new verified sellers relative to last seen set.
        // First load never triggers the shimmer — only subsequent polls.
        const currentIds = new Set(items.map((x) => x.user_id));
        if (seenIdsRef.current) {
          const freshly = [...currentIds].filter(
            (id) => !seenIdsRef.current.has(id)
          );
          if (freshly.length) {
            setJustVerifiedIds(new Set(freshly));
            setTimeout(() => {
              if (alive) setJustVerifiedIds(new Set());
            }, 4000);
          }
        }
        seenIdsRef.current = currentIds;
      } catch {
        /* silent — keep showing last good state */
      }
    };
    fetchAll();
    // 15s polling — with cache-bust on approval, a newly-approved seller
    // appears within 15s everywhere.
    const t = setInterval(fetchAll, 15_000);
    // Lightweight 30s tick so "2m ago" labels stay fresh between fetches.
    const tick = setInterval(() => setTick((v) => v + 1), 30_000);
    return () => {
      alive = false;
      clearInterval(t);
      clearInterval(tick);
    };
  }, []);

  const gmv = useCountUp(stats?.gmv_usd ?? 0, { format: formatUSD });
  // Verified sellers: show the exact number without count-up animation.
  const sellers = stats?.verified_sellers != null ? formatCount(stats.verified_sellers) : "—";
  const buyers = stats?.verified_buyers != null ? formatCount(stats.verified_buyers) : "—";
  const shipped = useCountUp(stats?.items_shipped ?? 0, { format: formatCount });
  const rating =
    stats?.avg_rating == null
      ? stats?.review_count === 0
        ? "New"
        : "—"
      : Number(stats.avg_rating).toFixed(1);

  const subline = stats?.live_streams_now
    ? `${stats.live_streams_now} stream${stats.live_streams_now === 1 ? "" : "s"} live · ${
        stats.active_listings_now ?? 0
      } active listings`
    : `${stats?.active_listings_now ?? 0} active listings`;

  return (
    <section
      className="bg-zinc-950 text-white border-y border-zinc-900 relative overflow-hidden"
      data-testid="home-stats-section"
    >
      {/* tiny pulse on update */}
      <div
        key={flash}
        className="pointer-events-none absolute inset-0 bg-[#1D78AB]/10 animate-[fadeOut_1.2s_ease-out_forwards] opacity-0"
        style={{ animationFillMode: "forwards" }}
      />
      <style>{`
        @keyframes fadeOut { 0% { opacity: 0.4 } 100% { opacity: 0 } }
        @keyframes verified-slide-in {
          0%   { opacity: 0; transform: translateY(6px) scale(0.97); }
          60%  { opacity: 1; }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes verified-shine {
          0%   { transform: translateX(-120%); }
          100% { transform: translateX(220%); }
        }
        .verified-just-in {
          animation: verified-slide-in 600ms cubic-bezier(.2,.9,.3,1.1) both;
        }
        .verified-shine-wrap {
          position: relative;
          overflow: hidden;
          border-radius: 999px;
        }
        .verified-shine-wrap::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 50%;
          height: 100%;
          background: linear-gradient(
            100deg,
            transparent 0%,
            rgba(255,255,255,0.95) 50%,
            transparent 100%
          );
          pointer-events: none;
          animation: verified-shine 1.3s ease-out 0.15s 1 both;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <p className="text-xs tracking-[0.22em] text-zinc-500 uppercase font-bold">
              BY THE NUMBERS
            </p>
            <p className="text-sm text-zinc-400 mt-1">{subline}</p>
          </div>
          <div
            className="inline-flex items-center gap-2 text-[11px] font-bold tracking-widest uppercase text-emerald-400"
            data-testid="home-stats-live-indicator"
            title={stats?.updated_at ? `Updated ${new Date(stats.updated_at).toLocaleTimeString()}` : ""}
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            Live · Auto-refreshing
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-10">
          <StatTile
            icon={TrendUpIcon}
            value={gmv}
            raw={stats?.gmv_usd}
            label="Total GMV"
            tone="text-emerald-400"
            testid="home-stat-total-gmv"
          />
          <StatTile
            icon={SealCheckIcon}
            value={sellers}
            label="Verified sellers"
            tone="text-[#4FC3F7]"
            testid="home-stat-verified-sellers"
          />
          <StatTile
            icon={HandshakeIcon}
            value={buyers}
            raw={stats?.verified_buyers}
            label="Verified buyers"
            tone="text-violet-400"
            testid="home-stat-verified-buyers"
          />
          <StatTile
            icon={PackageIcon}
            value={shipped}
            raw={stats?.items_shipped}
            label="Items shipped"
            tone="text-amber-400"
            testid="home-stat-items-shipped"
          />
          <StatTile
            icon={StarIcon}
            value={rating}
            label={
              stats?.review_count && stats.review_count > 0
                ? `Avg · ${stats.review_count} review${stats.review_count === 1 ? "" : "s"}`
                : "Avg seller rating"
            }
            tone="text-pink-400"
            testid="home-stat-avg-rating"
          />
        </div>

        {/* Bonus real-time row — proves liveness */}
        {stats && (
          <div
            className="mt-10 flex items-center gap-6 text-xs text-zinc-400 flex-wrap"
            data-testid="home-stats-realtime-strip"
          >
            <span className="inline-flex items-center gap-2" data-testid="home-stats-live-count">
              {stats.live_streams_now > 0 ? (
                <span
                  className="relative flex h-2 w-2"
                  aria-label="streams are live"
                  data-testid="live-dot-pulse"
                >
                  <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-80 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.8)]" />
                </span>
              ) : (
                <span className="h-2 w-2 rounded-full bg-zinc-700" aria-label="no streams live" />
              )}
              <span className="font-mono text-zinc-200">
                {stats.live_streams_now ?? 0}
              </span>{" "}
              live right now
            </span>
            <span className="opacity-30">·</span>
            <span>
              <span className="font-mono text-zinc-200">
                {stats.active_listings_now ?? 0}
              </span>{" "}
              active listings
            </span>
            {stats.updated_at && (
              <>
                <span className="opacity-30">·</span>
                <span>
                  Last updated {new Date(stats.updated_at).toLocaleTimeString()}
                </span>
              </>
            )}
          </div>
        )}

        <VerifiedBuyersBanner count={stats?.verified_buyers ?? 0} />

        {/* Recently verified — auto-scrolling sellers + buyers rows */}
        {recent.length > 0 && (
          <VerifiedMarquee items={recent} justVerifiedIds={justVerifiedIds} />
        )}
      </div>
    </section>
  );
}
